import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import { Grid, Tab, Tabs } from "@mui/material";
// Button/Typography/SearchField moved into DashboardHeaderBar (EE-238).
import DashboardHeaderBar from "components/Dashboard/DashboardHeaderBar";
import StatusFilterChips from "components/Dashboard/StatusFilterChips";
import InquiryDialog from "./InquiryDialog";
import { StatusAck } from "hooks/useInquiryStatusUpdates";
import { InquiryStatusEnum, InquiryType } from "types/InquiryTypes";
import OutsourcingTable from "./OutsourcingTable";
import InquiryFilterBar, {
  EMPTY_INQUIRY_FILTERS,
  InquiryFilterState,
} from "./InquiryFilterBar";
import useStateWithSessionStorage from "hooks/UseStateWithSessionStorage";
import AppStateContext from "contexts/AppStateContext";
import { apiGetInquiry, InquiryListScope } from "util/network/Inquiries";
import {
  INQUIRY_STATUS_COLORS,
  INQUIRY_STATUS_LABELS,
  OPEN_INQUIRY_STATUSES,
} from "./constants/inquiryStatusColors";
import AwaitingEnquiryTable from "./AwaitingEnquiryTable";
import useListView from "hooks/useListView";

const ALL_INQUIRY_STATUSES = Object.values(InquiryStatusEnum);

const INQUIRY_VIEWS = ["awaiting", "mine", "all"] as const;
type InquiryView = (typeof INQUIRY_VIEWS)[number];

type ParamsType = {
  id?: string;
};

// Outsourcing management UI
const OutsourcingManagementUI = () => {
  const { token } = useContext(AppStateContext);
  const history = useHistory();
  const { id } = useParams<ParamsType>();
  // EE-253 P2b: the buyer's pool of submitted requisition lines sits beside
  // the Mine / All tabs. Each tab is a ?view= of its own; a bare /inquiries
  // opens the one used last.
  const [view, setView] = useListView(
    "/inquiries",
    INQUIRY_VIEWS,
    "all",
    "inquiryView"
  );
  const awaiting = view === "awaiting";
  // Which inquiry the dialog is open on, if any. Only digits count: the path
  // took whatever followed the slash before, so /outsourcing/requisitions -
  // a link that was never a route - came through here as an id.
  const openId = id && /^\d+$/.test(id) ? Number(id) : undefined;
  // Persisted like the Requisition dashboard's search. The id no longer seeds
  // it: a link to one inquiry opens that inquiry, and leaving the number in
  // the search box left the board filtered to it on every later visit.
  const [searchValue, setSearchValue] = useStateWithSessionStorage<string>(
    "",
    "inquirySearchVal",
  );
  // Mine is what this user raised - a requisition they requested, or an
  // inquiry they created. All is the default here, unlike the Requisitions
  // tab: this board is the buyers' queue, and a buyer's own requests are the
  // narrower view.
  const scope: InquiryListScope = view === "mine" ? "mine" : "all";
  // EE-253: the open stages, i.e. everything a buyer still has to act on.
  // Derived from the enum rather than listed by hand - the previous literal
  // list silently became four dead strings when the enum was re-lettered, and
  // a persisted session value can still hold those, so it is sanitised on
  // read. Without that, anyone with a tab open across the deploy gets an
  // empty board and no error. The key is versioned for the same reason: a
  // list saved under the old shared "status" key by a pre-re-letter tab is
  // ignored rather than read back.
  const [statusFilter, setStatusFilter] = useStateWithSessionStorage<
    InquiryStatusEnum[]
  >(OPEN_INQUIRY_STATUSES, "inquiryStatus.v2");
  const validStatusFilter = useMemo(
    () => statusFilter.filter((s) => ALL_INQUIRY_STATUSES.includes(s)),
    [statusFilter]
  );
  useEffect(() => {
    if (validStatusFilter.length !== statusFilter.length) {
      setStatusFilter(
        validStatusFilter.length > 0 ? validStatusFilter : OPEN_INQUIRY_STATUSES
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validStatusFilter.length, statusFilter.length]);
  const [filters, setFilters] = useStateWithSessionStorage<InquiryFilterState>(
    EMPTY_INQUIRY_FILTERS,
    "inquiryFilters",
  );
  // Clicking a row opens the inquiry for viewing; editing is a step taken from
  // inside that dialog, the way the Requisitions tab works.
  const [inquiryView, setInquiryView] = useState<InquiryType>();
  const [refresh, setRefresh] = useState(false);
  // Opening an inquiry acknowledges its marker; the board patches that row.
  const [statusAck, setStatusAck] = useState<StatusAck>();

  // Space-separated `attr<op>value` tokens for the backend's generic
  // `filter` param (InquiryController.get_inquiries). Empty values are
  // dropped; a malformed token is silently discarded server-side, which is
  // why the dates get a full `%Y-%m-%dT%H:%M:%S` timestamp appended - a bare
  // yyyy-MM-dd would simply stop filtering with no error anywhere.
  const filter = useMemo(() => {
    const tokens: string[] = [];
    if (filters.partnerId) tokens.push(`partnerId=${filters.partnerId}`);
    if (filters.type) tokens.push(`type=${filters.type}`);
    if (filters.deliveryMethod)
      tokens.push(`deliveryMethod=${filters.deliveryMethod}`);
    if (filters.orderId) tokens.push(`orderId=${filters.orderId}`);
    if (filters.quoteId) tokens.push(`quoteId=${filters.quoteId}`);
    if (filters.createdFrom)
      tokens.push(`created>${filters.createdFrom}T00:00:00`);
    if (filters.createdTo) tokens.push(`created<${filters.createdTo}T23:59:59`);
    return tokens.join(" ");
  }, [filters]);

  // The URL says which inquiry is open, so a link, a row click and the
  // browser's Back button all agree on it. A row click already has the record,
  // so the dialog paints from the row and the path follows; a link arriving
  // cold has only the number, and the record is read for it here.
  const openInquiry = (inquiry: InquiryType) => {
    setInquiryView(inquiry);
    history.push(`/inquiries/${inquiry.id}`);
  };

  const closeInquiryView = () => {
    setInquiryView(undefined);
    // replace, not push: opening and closing a dialog should not leave a
    // trail of board entries for Back to walk through.
    if (openId !== undefined) history.replace(`/inquiries?view=${view}`);
  };

  useEffect(() => {
    if (openId === undefined) {
      setInquiryView(undefined);
      return;
    }
    // Already showing it - a row click set the record before the path moved.
    if (inquiryView?.id === openId) return;
    let cancelled = false;
    apiGetInquiry(token, openId)
      .then((detail) => {
        if (!cancelled) setInquiryView(detail);
      })
      // A number that is not an inquiry leaves the board as it is, with the
      // path still showing what was asked for.
      .catch(console.error);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, token]);

  // A save inside the dialog leaves it open on the record it just wrote - the
  // dialog re-reads the inquiry itself, so there is nothing to hand back here.
  // The board behind it does need to catch up.
  const savedInquiry = () => setRefresh(true);

  const toggleStatus = (status: InquiryStatusEnum) => {
    setStatusFilter(
      statusFilter.includes(status)
        ? statusFilter.filter((item) => item !== status)
        : [...statusFilter, status],
    );
  };

  const toggleAllStatuses = () => {
    setStatusFilter(
      statusFilter.length === ALL_INQUIRY_STATUSES.length
        ? []
        : [...ALL_INQUIRY_STATUSES],
    );
  };


  return (
    <>
      <InquiryDialog
        open={inquiryView !== undefined}
        inquiry={inquiryView}
        editable
        onClose={closeInquiryView}
        onSaved={savedInquiry}
        // A workflow action refreshes the board behind the dialog but leaves
        // it open: moving an inquiry forward is usually followed by another
        // action on the same inquiry, and closing the dialog made every one
        // of those a re-open.
        onChanged={() => setRefresh(true)}
        onStatusAck={setStatusAck}
      />

      <Grid container>
        <Grid item xs={12}>
          <DashboardHeaderBar
            title="Sourcing overview"
            search={{
              value: searchValue,
              onChange: setSearchValue,
              placeholder: "Search inquiries..",
            }}
          />
        </Grid>
        <Grid item xs={12} sx={{ paddingLeft: "1.5rem", paddingTop: "1rem" }}>
          <Tabs
            value={view}
            onChange={(_, next: InquiryView) => setView(next)}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Awaiting Inquiry" value="awaiting" data-testid="tab-awaiting-enquiry" />
            <Tab label="Mine" value="mine" />
            <Tab label="All" value="all" />
          </Tabs>
        </Grid>
        {awaiting && (
          <Grid item xs={12} sx={{ padding: "1rem 1.5rem 1.5rem 1.5rem" }}>
            <AwaitingEnquiryTable
              searchValue={searchValue}
              onCreated={(inquiryId) => {
                setRefresh(true);
                history.push(`/inquiries/${inquiryId}`);
              }}
            />
          </Grid>
        )}
        {!awaiting && (
        <>
        <Grid item xs={12} sx={{ padding: "1rem 1.5rem 0 1.5rem" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
            }}
          >
            <StatusFilterChips
              statuses={ALL_INQUIRY_STATUSES}
              colors={INQUIRY_STATUS_COLORS}
              labels={INQUIRY_STATUS_LABELS}
              selected={validStatusFilter}
              onToggle={toggleStatus}
              onToggleAll={toggleAllStatuses}
            />
            <InquiryFilterBar
              filters={filters}
              onChange={setFilters}
            />
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            padding: "1.5rem",
          }}
        >
          <OutsourcingTable
            refresh={refresh}
            setRefresh={setRefresh}
            openInquiry={openInquiry}
            searchValue={searchValue}
            statusFilter={validStatusFilter}
            filter={filter}
            scope={scope}
            closedAs={filters.closedAs || undefined}
            statusAck={statusAck}
          />
        </Grid>
        </>
        )}
      </Grid>
    </>
  );
};

export default OutsourcingManagementUI;
